import { Badge, Skeleton } from '@radix-ui/themes';
import type { TFunction } from 'i18next';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';
import LocaleContext from './hooks/LocaleContext';
import ApplicantCardSkeleton from './swiper/ApplicantCardSkeleton';

type Stage = {
  stage: number;
  name: string;
  color: string;
  show?: boolean;
  accepted?: boolean;
  tooltip?: string;
  status: Status[];
};

type Status = {
  stage: number;
  name: string;
  id: string;
  show?: boolean;
};

const getStageStatuses = (t: TFunction): Stage[] => [
  {
    stage: 1,
    name: t('common.stages.newApplicants.name'),
    color: 'warning',
    show: false,
    status: [
      { stage: 1, name: t('common.statuses.invited'), id: 'invited' },
      { stage: 1, name: t('common.statuses.opened_invitation'), id: 'opened_invitation' },
      { stage: 1, name: t('common.statuses.reminder_sent'), id: 'reminder_sent' },
      { stage: 1, name: t('common.statuses.reminder_opened'), id: 'reminder_opened' },
      { stage: 1, name: t('common.statuses.interview_started'), id: 'interview_started' },
    ],
  },
  {
    stage: 2,
    name: t('common.stages.hubertLeads.name'),
    color: 'warning',
    status: [
      { stage: 2, name: t('common.statuses.high_score'), id: 'high_score', show: false },
      { stage: 2, name: t('common.statuses.passed'), id: 'passed', show: false },
      { stage: 2, name: t('common.statuses.unaccepted'), id: 'unaccepted', show: false },
      { stage: 2, name: t('common.statuses.claimed'), id: 'claimed' },
    ],
  },
  {
    stage: 3,
    name: t('common.stages.acceptedLead.name'),
    color: 'info',
    accepted: true,
    tooltip: t('common.stages.acceptedLead.tooltip'),
    status: [
      { stage: 3, name: t('common.statuses.no_next'), id: 'no_next', show: false },
      { stage: 3, name: t('common.statuses.auto_qualified'), id: 'auto_qualified' },
      { stage: 3, name: t('common.statuses.sent_to_ats'), id: 'sent_to_ats' },
      { stage: 3, name: t('common.statuses.contact_attempt_1'), id: 'contact_attempt_1' },
      { stage: 3, name: t('common.statuses.contact_attempt_2'), id: 'contact_attempt_2' },
      { stage: 3, name: t('common.statuses.contact_attempt_3'), id: 'contact_attempt_3' },
      { stage: 3, name: t('common.statuses.pre_screen_scheduled'), id: 'pre_screen_scheduled' },
      { stage: 3, name: t('common.statuses.phone_interviewed'), id: 'phone_interviewed', show: false },
      { stage: 3, name: t('common.statuses.interview_scheduled'), id: 'interview_scheduled' },
      { stage: 3, name: t('common.statuses.test'), id: 'test' },
      { stage: 3, name: t('common.statuses.references'), id: 'references' },
      { stage: 3, name: t('common.statuses.offer'), id: 'offer' },
    ],
  },
  {
    stage: 4,
    name: t('common.stages.hired.name'),
    color: 'success',
    accepted: true,
    tooltip: t('common.stages.hired.tooltip'),
    status: [{ stage: 4, name: t('common.statuses.hired'), id: 'hired' }],
  },
  {
    stage: 5,
    name: t('common.stages.notHired.name'),
    color: 'danger',
    accepted: false,
    tooltip: t('common.stages.notHired.tooltip'),
    status: [
      { stage: 5, name: t('common.statuses.no_response'), id: 'no_response' },
      { stage: 5, name: t('common.statuses.failed_additional_screening'), id: 'failed_additional_screening' },
      { stage: 5, name: t('common.statuses.no_longer_interested'), id: 'no_longer_interested' },
      { stage: 5, name: t('common.statuses.not_good_enough'), id: 'not_good_enough' },
      { stage: 5, name: t('common.statuses.not_qualified'), id: 'not_qualified', show: false },
      { stage: 5, name: t('common.statuses.over_qualified'), id: 'over_qualified' },
      { stage: 5, name: t('common.statuses.failed_tests'), id: 'failed_tests' },
      { stage: 5, name: t('common.statuses.manually_rejected'), id: 'manually_rejected', show: false },
      { stage: 5, name: t('common.statuses.hired_elsewhere'), id: 'hired_elsewhere' },
      { stage: 5, name: t('common.statuses.offer_declined'), id: 'offer_declined' },
    ],
  },
  {
    stage: 5,
    name: t('common.stages.notHiredOld.name'),
    color: 'secondary',
    show: false,
    status: [{ stage: 5, name: t('common.statuses.old_application'), id: 'old_application', show: false }],
  },
];

const getStatusLabel = (id: string) => {
  for (const s of getStageStatuses(i18n.t)) {
    for (const statuses of s.status) {
      if (statuses.id === id) return statuses.name;
    }
  }
  return i18n.t('common.getStatus.fallbackLabel');
};
const getStatusObject = (id: string) => {
  for (const s of getStageStatuses(i18n.t)) {
    for (const statuses of s.status) {
      if (statuses.id === id) return s;
    }
  }
};
const getStatusColor = (id: string) => {
  const status = getStatusObject(id);

  if (!status) return undefined;

  let color: 'amber' | 'green' | 'red' | 'blue' | 'gray' | 'bronze' | undefined;
  switch (status?.color) {
    case 'warning':
      color = 'amber';
      break;
    case 'success':
      color = 'green';
      break;
    case 'danger':
      color = 'red';
      break;
    case 'info':
      color = 'blue';
      break;
    case 'skeleton':
    case 'secondary':
      color = 'gray';
      break;
    default:
      color = 'bronze';
      break;
  }

  return color;
};

const MockupJobs = ({ title }: { title?: boolean }) => {
  const [randomValues] = useState(() => {
    const numSkeletonJobs = Math.floor(Math.random() * (5 - 2) + 2);
    const numSkeletonApplicants = Array(numSkeletonJobs)
      .fill(0)
      .map(() => Math.floor(Math.random() * (5 - 2) + 2));
    return { numSkeletonJobs, numSkeletonApplicants };
  });

  return Array(randomValues.numSkeletonJobs)
    .fill(1)
    .map((_, jobi) => (
      <div key={`${jobi}-skeleton-counter`} className="my-2">
        {title && (
          <h2 className="truncate">
            <Skeleton className="" width={'80%'} />
          </h2>
        )}
        {Array(randomValues.numSkeletonApplicants[jobi])
          .fill(1)
          .map((_, i) => (
            <ApplicantCardSkeleton key={i} />
          ))}
      </div>
    ));
};

const getScore = ({
  application,
  threshold,
  size,
}: {
  application: { stage: number; score: number; status: string; candidate?: { firstName: string; lastName: string } };
  threshold: number;
  size: string;
}) => {
  size = size ? 'score-pill-' + size : 'score-pill-lg';
  const hasScore = typeof application.score === 'number';

  if (!application) {
    return <Skeleton style={{ borderRadius: '50%' }} height="2rem" width="2rem" />;
  }
  if (application.stage === 3 || application.stage === 4) {
    if (hasScore) return <span className={size + ' green '}>{Math.floor(application.score)}</span>;
    else return <span className={size + ' green '}>-</span>;
  } else if (application.stage === 5 && application.status === 'old_application') {
    return <span className={size + ' grey '}>{Math.floor(application.score)}</span>;
  } else if (application.stage === 5 && hasScore) {
    return <span className={size + ' red '}>{Math.floor(application.score)}</span>;
  } else if (application.stage === 5 && isNaN(Math.floor(application.score)))
    return <span className={size + ' red '}>-</span>;
  else {
    if (hasScore) {
      if (Math.floor(application.score) >= threshold) {
        return <span className={size + ' purple '}>{Math.floor(application.score)}</span>;
      } else if (0 < Math.floor(application.score) && Math.floor(application.score) < threshold) {
        return <span className={size + ' grey '}>{Math.floor(application.score)}</span>;
      } else if (isNaN(Math.floor(application.score))) return <span className={size + ' grey '}>-</span>;
    } else {
      const firstLetter = application.candidate?.firstName ? application.candidate.firstName.slice(0, 1) : '';
      const lastLetter = application.candidate?.lastName ? application.candidate.lastName.slice(0, 1) : '';
      return <span className={size + ' grey uppercase'}>{firstLetter + lastLetter}</span>;
    }
  }
};

const getStatus = (application: {
  status: string;
  accepted: boolean;
  activityLog: { actionType: string; actionSubType: string; message: string }[];
}) => {
  let text, badge: 'amber' | 'green' | 'red' | 'blue' | 'gray' | 'bronze' | undefined;
  if (application && application.accepted === true) {
    text = getStatusLabel(application.status);
    badge = getStatusColor(application.status);
  } else if (application && application.accepted === false) {
    text = getStatusLabel(application.status);
    badge = getStatusColor(application.status);
  } else if (application && application.status) {
    text = getStatusLabel(application.status);
    badge = getStatusColor(application.status);
  } else if (!application) {
    text = <Skeleton />;
    badge = 'gray';
  } else if (application.activityLog && application.activityLog.length > 0) {
    const lastActivity = application.activityLog[application.activityLog.length - 1];
    const actionType = lastActivity.actionType;
    const actionSubType = lastActivity.actionSubType;

    if (lastActivity.message && lastActivity.message !== '') {
      text = lastActivity.message;
      badge = 'gray';
    } else if (actionType === 'sms') {
      if (actionSubType === 'scheduled') {
        text = i18n.t('common.getStatus.smsScheduled');
        badge = 'gray';
      } else if (actionSubType === 'delivered') {
        text = i18n.t('common.getStatus.smsSent');
        badge = 'gray';
      } else if (actionSubType === 'failed') {
        text = i18n.t('common.getStatus.smsFailed');
        badge = 'red';
      }
    }
  } else {
    text = i18n.t('common.getStatus.fallbackLabel');
    badge = 'gray';
  }

  return (
    <Badge variant="soft" color={badge} radius="large">
      {text}
    </Badge>
  );
};

const CandidateName = ({
  application,
  className,
}: {
  application: { candidate?: { firstName: string; lastName: string } };
  className: string;
}) => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);

  if (!userSettings.settings?.anonymized && application.candidate) {
    // Create full name if first and last name are available, else use either first or last name depending on which one is available
    let fullName = '';

    if (application.candidate.firstName && application.candidate.lastName) {
      fullName = application.candidate.firstName + ' ' + application.candidate.lastName;
    } else if (application.candidate.firstName) {
      fullName = application.candidate.firstName;
    } else if (application.candidate.lastName) {
      fullName = application.candidate.lastName;
    }

    return <span className={className + ' capitalize truncate '}>{fullName}</span>;
  } else return <span>{t('common.candidateName.anonymized')}</span>;
};

const getRandomWidth = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const capitalizeName = (name: string) => {
  if (name) return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const sortByKey = <T,>(items: T[], getKey: (item: T) => string) => {
  return [...items].sort((a, b) => getKey(a).localeCompare(getKey(b)));
};

export {
  CandidateName,
  capitalizeName,
  getRandomWidth,
  getScore,
  getStageStatuses,
  getStatus,
  getStatusColor,
  getStatusObject,
  MockupJobs,
  sortByKey,
};
